import { useState, Fragment } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  ArrowLeftStartOnRectangleIcon,
  UserPlusIcon,
} from "@heroicons/react/20/solid";
import { Users } from "lucide-react";
import { useLocation, useMatch, useNavigate } from "react-router";
import * as React from "react";
import { Icon } from "@iconify/react";
import LogoutModal from "./LogoutModal";
import { useAuthStore } from "../../../store/useAuthStore";
import useIsMobile from "../../../utils/detectDevice";
import { HStack, VStack } from "../../../components/utils";
import Avatar from "./Avatar";
import { useGetUserProfile } from "../../../queries/userProfile";
import { useIsAccountUser } from "../../../queries/companyUsers";

export type FlyoutProfileProps = {
  profileImage?: string;
};

export function FlyoutProfile({ profileImage }: FlyoutProfileProps) {
  const [dialogLogout, setDialogLogout] = useState(false);
  const [show, setShown] = useState(false);
  const [dialogChangePassword, setDialogChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { isMobile } = useIsMobile(1025);

  const navigate = useNavigate();

  const contextRef = React.useRef<HTMLDivElement>(null);
  const contextRef2 = React.useRef<HTMLDivElement>(null);
  const { setAuth, role, user, clear } = useAuthStore();
  const { data: userProfileDetails } = useGetUserProfile();
  const { isAccountUser } = useIsAccountUser();

  const routes = [
    // {
    //   name: "Change Password",
    //   onClick: () => {
    //     setDialogChangePassword(true);
    //     setShowPassword(false);
    //   },
    //   type: "iconify",
    //   icon: "ic:baseline-lock-reset",
    // },
    ...(isAccountUser
      ? [
          {
            name: "User Management",
            onClick: () => {
              navigate("/setting/users");
              setShown(false);
            },
            type: "heroIcon",
            icon: Users,
          },
        ]
      : []),
    {
      name: "Log Out",
      onClick: () => {
        setDialogLogout(true);
        setShown(false);
      },
      type: "heroIcon",
      icon: ArrowLeftStartOnRectangleIcon,
    },
  ];

  const profileMatch = useMatch(`/${"/user/profile".split("/")[1]}/*`);
  const { pathname } = useLocation();

  return (
    <div>
      {false ? (
        <div className="items-center px-6 py-2">
          <p className="text-sm animate-pulse font-medium">Switching...</p>
        </div>
      ) : (
        <Popover className="relative">
          {dialogLogout && (
            <LogoutModal
              isOpen={dialogLogout}
              onClose={() => {
                setDialogLogout(false);
              }}
            />
          )}
          {/* TODO: need to navigate to Change Password Page */}

          {/* {dialogChangePassword && (
            <ChangePasswordModal
              isOpen={dialogChangePassword}
              onClose={() => {
                setDialogChangePassword(false);
              }}
            />
          )} */}
          <Popover className="relative">
            <div>
              <PopoverButton
                onClick={() => setShown(!show)}
                onMouseEnter={() => {
                  setShown(true);
                }}
                onMouseLeave={() => {
                  setShown(false);
                }}
                className={`-ml-1.5 flex gap-2 cursor-pointer items-center p-1 focus:outline-none ${
                  pathname !== "/profile"
                    ? "bg-white text-secondary"
                    : "bg-secondary text-white"
                } `}
              >
                <Avatar name={userProfileDetails?.fullName || ""} profileImage={profileImage} />
                {/* {!isMobile && <><p className="text-sm text-gray-700">{name}</p>
                <span className="hidden lg:flex lg:items-center">
                  <ChevronDownIcon
                    className="ml-2 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
                </>} */}
              </PopoverButton>
            </div>
            <Transition
              show={show}
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                onMouseEnter={() => {
                  setShown(true);
                }}
                onMouseLeave={() => {
                  setShown(false);
                }}
                ref={contextRef}
                className="absolute z-10 mt-5 flex w-screen max-w-max  right-0 "
              >
                <div className="w-screen max-w-xs flex-auto overflow-hidden rounded-lg bg-white text-sm leading-6 shadow-lg">
                  <div className="divide-x divide-gray-900/5 bg-gray-50">
                    <div className="group relative flex gap-x-2 rounded-lg p-4 hover:bg-gray-50">
                      <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                        <Avatar name={userProfileDetails?.fullName || ""} profileImage={profileImage}/>
                      </div>
                      <VStack className="w-full">
                        <HStack className="justify-between items-center w-full">
                          <a className="font-semibold text-gray-900 flex-1">
                            {userProfileDetails?.fullName}

                            <span className="absolute inset-0" />
                          </a>

                          <p className="ml-1 text-sm text-gray-500 text-ellipsis overflow-clip">
                            {role}
                          </p>
                        </HStack>

                        <p className="text-gray-600 whitespace-nowrap">
                          {userProfileDetails?.emailAddress}
                        </p>
                        <p className="text-sm text-gray-500 text-ellipsis overflow-clip"></p>
                      </VStack>
                    </div>
                  </div>

                  <div className="p-2  cursor-pointer">
                    {routes.map((item) => (
                      <div
                        key={item.name}
                        onClick={() => {
                          item.onClick();
                        }}
                        className="group relative flex gap-x-4 rounded-lg p-2 hover:bg-gray-50 items-center"
                      >
                        <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                          {item?.type !== "iconify" && (
                            <item.icon
                              className={`h-6 w-6 text-gray-600 group-hover:text-primary ${
                                profileMatch && item.name === "Profile"
                                  ? "text-primary"
                                  : ""
                              }`}
                              aria-hidden="true"
                            />
                          )}
                          {item?.type === "iconify" &&
                            typeof item.icon === "string" && (
                              <Icon
                                icon={item.icon || ""}
                                className={`h-6 w-6 text-gray-600 group-hover:text-primary ${
                                  profileMatch && item.name === "Profile"
                                    ? "fill-primary"
                                    : ""
                                }`}
                              />
                            )}
                        </div>
                        <div>
                          <a
                            className={`font-semibold text-gray-900 group-hover:text-primary ${
                              profileMatch && item.name === "Profile"
                                ? "text-primary"
                                : ""
                            }`}
                          >
                            {item.name}
                            <span className="absolute inset-0" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </Popover>
        </Popover>
      )}
    </div>
  );
}
